package diana.soleil.design.iraniancanadienbazzar.core.model

import kotlinx.serialization.Serializable

/**
 * Hidden Gems — admin-curated cultural content (Persian humor + regional recipes). Content is
 * national (`metroId: null` on the wire, so no metro field here); FA is the canonical language and
 * EN/FR are transcreations ([LocalizedText] + `resolve`). See `docs/HIDDEN_GEMS_PLAN.md` and
 * `docs/DATA_MODEL.md` § Hidden Gems.
 */
@Serializable
data class Joke(
    val id: String,
    val text: LocalizedText = LocalizedText(),
    val category: JokeCategory = JokeCategory.Observational,
    val maturity: JokeMaturity = JokeMaturity.Clean,
    val tags: List<String> = emptyList(),
    /** Function-maintained public like count (never written by clients). */
    val likeCount: Int = 0,
    val publishedAtMillis: Long = 0L,
)

/** Joke genres (wire strings in `docs/DATA_MODEL.md`); unknown values decode as [Observational]. */
@Serializable
enum class JokeCategory { Classic, Wordplay, Family, Nostalgia, Observational }

/** Content tier: [Adult] is dark-launched behind `gems_adult_jokes_enabled` + an 18+ opt-in. */
@Serializable
enum class JokeMaturity { Clean, Adult }

/**
 * A **Danestaniha** (دانستنی‌ها, "things worth knowing") card — a bite-sized "did you know?" fact
 * about Iran, Canada, and the Iranian-Canadian community. Structurally a text atom like [Joke]:
 * FA is the canonical language, EN/FR are transcreations. [source] is an optional short attribution
 * or context line (author content, resolved via `Localizer`). See `docs/HIDDEN_GEMS_PLAN.md`.
 */
@Serializable
data class Fact(
    val id: String,
    val text: LocalizedText = LocalizedText(),
    val category: FactCategory = FactCategory.Iran,
    /** Optional one-line source/context (e.g. "Statistics Canada, 2021"); author content, localized. */
    val source: LocalizedText? = null,
    val tags: List<String> = emptyList(),
    /** Function-maintained public like count (never written by clients). */
    val likeCount: Int = 0,
    val publishedAtMillis: Long = 0L,
)

/**
 * Danestaniha topics; unknown wire values decode as [Iran] so a newly-seeded topic still renders.
 * Display labels are localized string resources, never these names.
 */
@Serializable
enum class FactCategory(val wire: String) {
    Iran("iran"),
    Canada("canada"),
    Community("community"),
    History("history"),
    Language("language"),
    Nature("nature");

    companion object {
        fun fromWire(value: String?): FactCategory =
            entries.firstOrNull { it.wire.equals(value, ignoreCase = true) } ?: Iran
    }
}

/** One ingredient line: both parts are author-localized content, not app chrome. */
@Serializable
data class RecipeIngredient(
    val name: LocalizedText = LocalizedText(),
    val quantity: LocalizedText = LocalizedText(),
)

/** One numbered instruction; [imageUrl] is an optional step photo (our Storage only). */
@Serializable
data class RecipeStep(
    val text: LocalizedText = LocalizedText(),
    val imageUrl: String? = null,
)

@Serializable
data class Recipe(
    val id: String,
    val title: LocalizedText = LocalizedText(),
    val summary: LocalizedText = LocalizedText(),
    /** Optional cultural/regional backstory — the "gem" flavor. */
    val story: LocalizedText? = null,
    val region: RecipeRegion = RecipeRegion.Other,
    val category: RecipeCategory = RecipeCategory.Other,
    /** Rehosted in our Storage (`gems/recipes/...`) — never third-party URLs (web CORS). */
    val imageUrl: String? = null,
    val prepMinutes: Int = 0,
    val cookMinutes: Int = 0,
    val servings: Int = 0,
    val difficulty: RecipeDifficulty = RecipeDifficulty.Medium,
    /** Seed query for the "find ingredients near you" directory deep link. */
    val groceryQuery: String? = null,
    val ingredients: List<RecipeIngredient> = emptyList(),
    val steps: List<RecipeStep> = emptyList(),
    val tags: List<String> = emptyList(),
    val likeCount: Int = 0,
    val publishedAtMillis: Long = 0L,
) {
    val totalMinutes: Int get() = prepMinutes + cookMinutes
}

/**
 * Canonical region keys for the recipe browser's "journey across Iran" chip rail. Display labels
 * are app-chrome string resources (localized), never these names. Unknown keys decode as [Other]
 * so a new region seeded before an app update still renders (label falls back to the wire key).
 */
@Serializable
enum class RecipeRegion(val wire: String) {
    Gilan("gilan"),
    Mazandaran("mazandaran"),
    Tabriz("tabriz"),
    Ardabil("ardabil"),
    Isfahan("isfahan"),
    Shiraz("shiraz"),
    Yazd("yazd"),
    Kerman("kerman"),
    Kermanshah("kermanshah"),
    Kurdistan("kurdistan"),
    Khuzestan("khuzestan"),
    Mashhad("mashhad"),
    Tehran("tehran"),
    Qazvin("qazvin"),
    Hormozgan("hormozgan"),
    Other("other");

    companion object {
        fun fromWire(value: String?): RecipeRegion =
            entries.firstOrNull { it.wire.equals(value, ignoreCase = true) } ?: Other
    }
}

/** Recipe categories; unknown wire values decode as [Other]. */
@Serializable
enum class RecipeCategory(val wire: String) {
    Sweet("sweet"),
    Stew("stew"),
    Rice("rice"),
    Bread("bread"),
    Appetizer("appetizer"),
    Drink("drink"),
    Preserve("preserve"),
    Other("other");

    companion object {
        fun fromWire(value: String?): RecipeCategory =
            entries.firstOrNull { it.wire.equals(value, ignoreCase = true) } ?: Other
    }
}

/** Cooking difficulty; unknown wire values decode as [Medium]. */
@Serializable
enum class RecipeDifficulty(val wire: String) {
    Easy("easy"),
    Medium("medium"),
    Hard("hard");

    companion object {
        fun fromWire(value: String?): RecipeDifficulty =
            entries.firstOrNull { it.wire.equals(value, ignoreCase = true) } ?: Medium
    }
}

/**
 * A **Shahnameh** tale — a prose retelling of a story from Ferdowsi's epic (Rostam & Sohrab,
 * Zahhak & Kaveh, Zal & Rudabeh…). Long-form, so it's read in a browser→detail flow (like [Recipe])
 * rather than the swipe pager. FA is canonical; EN/FR are faithful retellings, not verse translation.
 */
@Serializable
data class Tale(
    val id: String,
    val title: LocalizedText = LocalizedText(),
    /** 1–2 line hook for the browser card. */
    val summary: LocalizedText = LocalizedText(),
    /** The full retelling (multiple paragraphs). */
    val body: LocalizedText = LocalizedText(),
    /** Optional one-line theme/lesson. */
    val moral: LocalizedText? = null,
    /** Rehosted Persian-miniature hero art (our Storage) — never third-party URLs (web CORS). */
    val imageUrl: String? = null,
    /** Character keys (rostam, sohrab…) shown as chips; lowercase. */
    val characters: List<String> = emptyList(),
    val likeCount: Int = 0,
    val publishedAtMillis: Long = 0L,
)

/**
 * A **poet** in the Persian poetry section — Hafez, Saadi, Rumi, Ferdowsi, Khayyam, Nezami, Parvin.
 * Only poets whose work is in the public domain in Canada (life + 50 years); the FA text is the
 * original verse and en/fr are our own plain translations, so nothing here carries a third party's
 * rights. Tapping a poet opens their [Poem]s.
 */
@Serializable
data class Poet(
    val id: String,
    val name: LocalizedText = LocalizedText(),
    /** "14th century" / "قرن هشتم" — one short line under the name. */
    val era: LocalizedText = LocalizedText(),
    /** 2–4 sentences: who they were and why they still matter. */
    val bio: LocalizedText = LocalizedText(),
    /** Rehosted portrait/miniature art (our Storage) — never a third-party URL (web CORS). */
    val imageUrl: String? = null,
    /** Hub ordering; lower sorts first (chronological by default). */
    val orderIndex: Int = 0,
    val publishedAtMillis: Long = 0L,
)

/** Classical Persian verse forms; unknown wire values decode as [Other]. */
@Serializable
enum class PoemForm(val wire: String) {
    Ghazal("ghazal"),
    Robaei("robaei"),
    Masnavi("masnavi"),
    Qasideh("qasideh"),
    Qete("qete"),
    Other("other");

    companion object {
        fun fromWire(value: String?): PoemForm =
            entries.firstOrNull { it.wire.equals(value, ignoreCase = true) } ?: Other
    }
}

/**
 * One poem (or a self-contained excerpt) by a [Poet]. [body] keeps line breaks — FA is the canonical
 * verse, en/fr are plain-language translations rather than metered verse, matching how [Tale] treats
 * the Shahnameh.
 */
@Serializable
data class Poem(
    val id: String,
    val poetId: String = "",
    val title: LocalizedText = LocalizedText(),
    /** The verse itself; newline-separated lines, rendered as typed. */
    val body: LocalizedText = LocalizedText(),
    /** Optional one-line context ("written after the death of his son"). */
    val note: LocalizedText? = null,
    val form: PoemForm = PoemForm.Other,
    /**
     * The fal reading: what this ghazal is taken to say to someone who opened the Divan with a
     * question in mind. Only Hafez poems carry one, and only those are drawn by Fal-e Hafez.
     */
    val fal: LocalizedText? = null,
    val likeCount: Int = 0,
    val publishedAtMillis: Long = 0L,
)

/** Where a [Story] comes from; unknown wire values decode as [Other]. */
@Serializable
enum class StoryRegion(val wire: String) {
    Gilan("gilan"),
    Mazandaran("mazandaran"),
    Azerbaijan("azerbaijan"),
    Kurdistan("kurdistan"),
    Khorasan("khorasan"),
    Fars("fars"),
    Isfahan("isfahan"),
    Sistan("sistan"),
    Khuzestan("khuzestan"),
    Other("other");

    companion object {
        fun fromWire(value: String?): StoryRegion =
            entries.firstOrNull { it.wire.equals(value, ignoreCase = true) } ?: Other
    }
}

/**
 * A short folk story from one corner of Iran — our own retelling of a traditional tale (the same
 * rights model as [Tale]: traditional material, our words, so no third-party text is reproduced).
 */
@Serializable
data class Story(
    val id: String,
    val title: LocalizedText = LocalizedText(),
    /** 1–2 line hook for the browser card. */
    val summary: LocalizedText = LocalizedText(),
    /** The full retelling (multiple paragraphs). */
    val body: LocalizedText = LocalizedText(),
    val region: StoryRegion = StoryRegion.Other,
    /** Optional one-line theme/lesson. */
    val moral: LocalizedText? = null,
    /** Rehosted hero art (our Storage) — never a third-party URL (web CORS). */
    val imageUrl: String? = null,
    val likeCount: Int = 0,
    val publishedAtMillis: Long = 0L,
)

/** Type discriminator for a saved (bookmarked) gem — `users/{uid}/savedGems/{gemId}.type`. */
@Serializable
enum class GemType(val wire: String) {
    Joke("joke"),
    Recipe("recipe"),
    Fact("fact"),
    Tale("tale"),
    Poem("poem"),
    Story("story");

    companion object {
        fun fromWire(value: String?): GemType? =
            entries.firstOrNull { it.wire.equals(value, ignoreCase = true) }
    }
}

/** A private gem bookmark (joke or recipe), synced at `users/{uid}/savedGems/{gemId}`. */
@Serializable
data class SavedGem(val id: String, val type: GemType) {
    /** Stable local-preferences encoding (`joke:{id}` / `recipe:{id}`). */
    val encoded: String get() = "${type.wire}:$id"

    companion object {
        fun decode(raw: String): SavedGem? {
            val separator = raw.indexOf(':')
            if (separator <= 0 || separator == raw.length - 1) return null
            val type = GemType.fromWire(raw.substring(0, separator)) ?: return null
            return SavedGem(id = raw.substring(separator + 1), type = type)
        }
    }
}
